import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../../../context/AuthProvider';
import { ProjectData } from '../api/groupProject';
import {
  ChatbotStudent,
  getGroupTemplates,
  getStudentsProjects,
  GroupTemplate,
  login,
  Section,
  StudentProject,
} from '../api/studentData';
import groupProject from '../api/groupProject';
import { StudyGroupQuery } from '../../../../../../generated/graphqlModels';

const useFileServerStudentTeams = (
  students: StudyGroupQuery['studyGroup']['students'],
  isProgramUsingFileServer: boolean,
  programFileServerNamespace: string,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const user = useContext(AuthContext);
  const [groupTemplates, setGroupTemplates] = useState<GroupTemplate[]>([]);
  const [missingStudents, setMissingStudents] = useState<
    StudyGroupQuery['studyGroup']['students']
  >([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [fileServerStudents, setFileServerStudents] = useState<
    ChatbotStudent[]
  >([]);
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [studentProjects, setStudentProjects] = useState<
    Record<string, StudentProject[]>
  >({});
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!isProgramUsingFileServer) {
      return;
    }
    if (user) {
      getInitialData();
    }
  }, [user]);

  const getInitialData = async () => {
    if (!user) {
      return;
    }
    setIsLoading(true);
    try {
      const filesUser = await login(user.token);
      groupProject.setToken(filesUser.token);
      setToken(filesUser.token);
      const studentData = await getStudentsProjects(
        [...students.map((s) => s.pk), filesUser.id, filesUser.platformId],
        filesUser.token,
      );
      setFileServerStudents(studentData.students);
      Object.values(studentData.studentProjects)
        .flat()
        .forEach((studentProject) => {
          if (
            studentProject.type === 'RASA' &&
            studentProject.attributes.answers_id
          ) {
            studentProject.attributes.answers_id = null;
          }
        });
      setStudentProjects(studentData.studentProjects);
      setSections(studentData.sections);
      setProjects(
        studentData.studentProjects.groupProjects!.map((p) => ({
          ...p,
          collaboratorIds: p.collaborators!.map((c) => c.platformId),
        })),
      );
      const newGroupTemplates = await getGroupTemplates(filesUser.token);
      const newMissingStudents = (students || []).filter((student) => {
        return !studentData.students.find(
          (chatbotStudent) => chatbotStudent.platformId === student.pk,
        );
      });
      const filteredNewGroupTemplates = newGroupTemplates.filter(
        (g) => g.namespace === programFileServerNamespace,
      );
      setGroupTemplates(filteredNewGroupTemplates);
      setMissingStudents(newMissingStudents);
    } catch (e) {
      console.log(e);
    }
    setIsLoading(false);
  };

  const setAnswersId = (projectId: string, answersId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      project.attributes.answers_id = answersId;
      setProjects([...projects]);
    }
    const studentProject = Object.values(studentProjects)
      .flat()
      .find((p) => p.id === projectId);
    if (studentProject) {
      studentProject.attributes.answers_id = answersId;
      setStudentProjects({ ...studentProjects });
    }
  };

  const allTemplateIds = groupTemplates.map((t) => t.id);
  const mainGroupTemplateId = groupTemplates[0]?.id || '';
  const filteredStudentProjects = {
    ...studentProjects,
  } as typeof studentProjects;
  Object.keys(filteredStudentProjects).forEach((key) => {
    filteredStudentProjects[key] = filteredStudentProjects[key]!.filter((p) =>
      allTemplateIds.includes(p.templateId),
    );
  });

  const filteredProjects = projects.filter(
    (p) => p.templateId === mainGroupTemplateId,
  );

  return {
    missingStudents,
    sections,
    fileServerStudents,
    isLoading,
    token,
    sessionId: user?.session_id,
    mainGroupTemplateId,
    studentProjects: filteredStudentProjects,
    projects: filteredProjects,
    groupTemplates,
    userPk: user?.pk,
    getInitialData,
    setAnswersId,
  };
};

export default useFileServerStudentTeams;
